import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Search } from 'lucide-react';
import { EmptyState, PageHeader, SkeletonList } from '@/components/ui';
import { CategoryCard } from '@/components/CategoryCard';
import { ProgressSummaryCard } from '@/features/study/ProgressSummaryCard';
import { SignListItem } from '@/components/SignListItem';
import { useCatalog } from '@/features/sync/CatalogProvider';
import {
  useCategories,
  useCategoryStats,
  useFavoriteSigns,
  useRecentSigns,
  useSignStates,
} from '@/hooks/useCatalogQueries';

function SectionHeader({
  title,
  to,
}: {
  title: string;
  to?: string;
}): React.ReactElement {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h2>
      {to && (
        <Link to={to} className="flex items-center gap-0.5 text-sm text-sky-400">
          Ver tudo
          <ChevronRight size={16} aria-hidden />
        </Link>
      )}
    </div>
  );
}

export function HomePage(): React.ReactElement {
  const { phase, syncing } = useCatalog();
  const categories = useCategories();
  const stats = useCategoryStats();
  const favorites = useFavoriteSigns();
  const recent = useRecentSigns(5);

  const visibleIds = useMemo(
    () => [...(favorites ?? []).slice(0, 5).map((sign) => sign.id), ...(recent ?? []).map((entry) => entry.sign.id)],
    [favorites, recent],
  );
  const states = useSignStates(visibleIds);

  const loading = phase === 'loading' || categories === undefined;

  return (
    <>
      <PageHeader title="Início" showConnection />

      {/* Campo de busca: leva para a tela dedicada em vez de filtrar aqui,
          para que o teclado do iPhone não empurre a Home inteira. */}
      <Link
        to="/search"
        className="flex min-h-12 items-center gap-2 rounded-xl bg-slate-800 px-4 text-slate-400 active:bg-slate-700"
      >
        <Search size={18} aria-hidden />
        Procurar sinal
      </Link>

      {loading && <SkeletonList rows={6} />}

      {!loading && categories.length === 0 && (
        <EmptyState
          icon={<BookOpen size={40} aria-hidden />}
          title={syncing ? 'Carregando o glossário…' : 'Glossário ainda não carregado'}
          description={
            syncing
              ? 'Buscando o catálogo pela primeira vez.'
              : 'Conecte-se à internet uma vez para baixar o catálogo. Depois disso, ele funciona offline.'
          }
          action={
            <Link
              to="/settings"
              className="min-h-11 rounded-lg bg-sky-500 px-5 py-3 text-sm font-medium text-slate-950"
            >
              Ir para Ajustes
            </Link>
          }
        />
      )}

      {!loading && categories.length > 0 && (
        <>
          <ProgressSummaryCard />

          {favorites && favorites.length > 0 && (
            <>
              <SectionHeader title="Favoritos" to="/favorites" />
              <ul className="flex flex-col">
                {favorites.slice(0, 5).map((sign) => (
                  <SignListItem key={sign.id} sign={sign} state={states?.get(sign.id)} />
                ))}
              </ul>
            </>
          )}

          {recent && recent.length > 0 && (
            <>
              <SectionHeader title="Recentes" to="/recent" />
              <ul className="flex flex-col">
                {recent.map(({ sign }) => (
                  <SignListItem key={sign.id} sign={sign} state={states?.get(sign.id)} />
                ))}
              </ul>
            </>
          )}

          <SectionHeader title="Categorias" />
          <ul className="flex flex-col gap-2 pb-4">
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                learned={stats?.get(category.id)?.learned ?? 0}
                offlineAvailable={stats?.get(category.id)?.offlineAvailable ?? 0}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}
